import { useCallback, useEffect, useRef, useState } from 'react';
import { coerceEngineConfig, defaultEngineConfig, type EngineCatalogEntry, type EngineConfig } from '@lapo/core';
import type { ApiClient } from './auth/apiClient.js';

/**
 * Owns the Automations (engine-pipeline) config — which processing engines are enabled and in what
 * order — plus the read-only catalog of available engines. Like `useToolbarConfig`, it paints from a
 * localStorage cache, reconciles with the server (`/me/engine-config` + `/engines/catalog`), and writes
 * optimistically + debounced. `api` null (signed out) keeps it purely local (catalog then empty).
 */
const STORAGE_KEY = 'lapo-engine-config';
const SAVE_DEBOUNCE_MS = 800;

type EngineApi = Pick<ApiClient, 'getEngineCatalog' | 'getEngineConfig' | 'putEngineConfig'>;

function loadCache(): EngineConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return coerceEngineConfig(raw ? JSON.parse(raw) : undefined);
  } catch {
    return defaultEngineConfig();
  }
}

function saveCache(config: EngineConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* storage unavailable (private mode); keep running in-memory */
  }
}

export interface UseEngineConfig {
  catalog: EngineCatalogEntry[];
  config: EngineConfig;
  /** Replace the config, persist to cache immediately, and debounce a server PUT. */
  update: (config: EngineConfig) => void;
}

export function useEngineConfig(api: EngineApi | null): UseEngineConfig {
  const [config, setConfig] = useState<EngineConfig>(loadCache);
  const [catalog, setCatalog] = useState<EngineCatalogEntry[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reconcile catalog + config with the server once the api is available. The cache already painted;
  // the server value wins when it answers. Failures keep the local cache (offline-first).
  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    void api
      .getEngineCatalog()
      .then((res) => !cancelled && setCatalog(res.engines))
      .catch(() => {
        /* offline / unauthorized → no catalog to render */
      });
    void api
      .getEngineConfig()
      .then((remote) => {
        if (cancelled) return;
        const coerced = coerceEngineConfig(remote);
        setConfig(coerced);
        saveCache(coerced);
      })
      .catch(() => {
        /* offline / unauthorized → keep the local cache */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const update = useCallback(
    (next: EngineConfig) => {
      const coerced = coerceEngineConfig(next);
      setConfig(coerced);
      saveCache(coerced);
      if (api) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
          void api.putEngineConfig(coerced).catch(() => {
            /* offline → the cache holds; next successful load reconciles */
          });
        }, SAVE_DEBOUNCE_MS);
      }
    },
    [api],
  );

  return { catalog, config, update };
}
